import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import PosterModal from './components/PosterModal';

interface InvitedMember {
  id: string;
  nickname: string | null;
  avatar_url: string | null;
  created_at: string;
}

const InvitePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [invitedMembers, setInvitedMembers] = useState<InvitedMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPoster, setShowPoster] = useState(false);

  const inviteCode = user
    ? 'FZY' + user.id.replace(/-/g, '').slice(-6).toUpperCase()
    : '---';
  const inviteLink = `${window.location.origin}/login?invite=${inviteCode}`;

  const fetchInvited = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('custom_users')
      .select('id, nickname, avatar_url, created_at')
      .eq('referrer_id', user.id)
      .order('created_at', { ascending: false });
    setInvitedMembers((data as InvitedMember[]) ?? []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchInvited(); }, [fetchInvited]);

  const handleCopy = (text: string, type: 'code' | 'link') => {
    // navigator.clipboard 仅在 HTTPS 下可用，HTTP 环境降级用 execCommand
    const doCopy = () => {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
      }
      // HTTP 降级方案
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      return Promise.resolve();
    };
    doCopy().catch(() => {});
    if (type === 'code') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const avatarChar = (name: string | null) => (name ?? '?')[0].toUpperCase();
  const avatarColors = [
    'from-rose-400 to-pink-500', 'from-orange-400 to-amber-500',
    'from-emerald-400 to-teal-500', 'from-violet-400 to-purple-500',
    'from-sky-400 to-cyan-500', 'from-red-400 to-rose-500',
  ];
  const avatarGradient = (name: string | null) => {
    const idx = (name ?? '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % avatarColors.length;
    return avatarColors[idx];
  };

  const steps = [
    { icon: 'ri-share-line', title: '分享邀请码', desc: '将您的专属邀请码或海报分享给好友' },
    { icon: 'ri-user-add-line', title: '好友注册', desc: '好友注册时填写您的邀请码' },
    { icon: 'ri-team-line', title: '建立团队', desc: '好友成为您的直属团队成员' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center px-4 h-14 gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-gray-700 text-xl" />
          </button>
          <h1 className="font-bold text-gray-900 text-base">邀请好友</h1>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-emerald-500 to-emerald-400 px-4 pt-6 pb-10 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
          <i className="ri-user-add-line text-white text-3xl" />
        </div>
        <h2 className="text-white font-bold text-xl mb-1">邀好友 · 共成长</h2>
        <p className="text-white/80 text-sm">邀请好友加入，壮大您的团队</p>
      </div>

      {/* Invite Card */}
      <div className="mx-4 -mt-6 relative z-10 bg-white rounded-2xl p-5">
        <p className="text-xs text-gray-500 mb-2 text-center">我的专属邀请码</p>
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className="text-3xl font-bold text-emerald-600 tracking-widest">{inviteCode}</span>
          <button
            onClick={() => handleCopy(inviteCode, 'code')}
            className="bg-emerald-50 text-emerald-600 text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap font-medium"
          >
            {copied ? <span className="flex items-center gap-1"><i className="ri-check-line" />已复制</span> : '复制'}
          </button>
        </div>
        <div className="bg-gray-50 rounded-xl px-3 py-2.5 flex items-center justify-between mb-4">
          <span className="text-xs text-gray-500 truncate flex-1 mr-2">{inviteLink}</span>
          <button
            onClick={() => handleCopy(inviteLink, 'link')}
            className="text-emerald-600 text-xs font-medium cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            {copiedLink ? <span className="flex items-center gap-1"><i className="ri-check-line" />已复制</span> : '复制链接'}
          </button>
        </div>

        {/* 生成海报按钮 */}
        <button
          onClick={() => setShowPoster(true)}
          className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-image-line text-base" />
          生成分享海报
        </button>

        {/* 已邀请统计 */}
        {invitedMembers.length > 0 && (
          <div className="bg-emerald-50 rounded-xl px-4 py-3 flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <i className="ri-team-line text-emerald-500 text-base" />
              <span className="text-sm text-emerald-700 font-medium">已邀请 {invitedMembers.length} 位好友</span>
            </div>
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="bg-white mt-3 mx-4 rounded-2xl p-5">
        <h3 className="font-bold text-gray-900 text-sm mb-4">邀请流程</h3>
        <div className="space-y-4">
          {steps.map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl flex-shrink-0">
                <i className={`${step.icon} text-emerald-600 text-lg`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center bg-emerald-500 text-white text-xs rounded-full font-bold flex-shrink-0">{i + 1}</span>
                  <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-7">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invited Friends */}
      <div className="bg-white mt-3 mx-4 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-sm">
            直推成员
            {!loading && <span className="text-gray-400 font-normal ml-1">（{invitedMembers.length}人）</span>}
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : invitedMembers.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-gray-400">
            <i className="ri-user-add-line text-4xl mb-2 text-gray-200" />
            <p className="text-sm text-gray-500 mb-1">还没有邀请好友</p>
            <p className="text-xs text-gray-400">分享邀请码或海报，好友注册后加入您的团队</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invitedMembers.map((member) => (
              <div key={member.id} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.nickname ?? ''} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${avatarGradient(member.nickname)}`}>
                      <span className="text-white font-bold text-sm">{avatarChar(member.nickname)}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{member.nickname ?? '用户'}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(member.created_at).toLocaleDateString('zh-CN')} 加入
                  </p>
                </div>
                <span className="text-emerald-600 text-xs font-medium bg-emerald-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                  已加入
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Poster Modal */}
      {showPoster && (
        <PosterModal
          inviteCode={inviteCode}
          nickname={user?.nickname ?? ''}
          onClose={() => setShowPoster(false)}
        />
      )}
    </div>
  );
};

export default InvitePage;
